import React from 'react'
import {
    Box,
    Dialog,
    DialogTitle as MuiDialogTitle,
    DialogContent,
    Button,
    IconButton,
    Grid,
    Typography,
    Tooltip,
} from '@mui/material'
import { Close as CloseIcon, HelpOutline as HelpOutlineIcon } from '@mui/icons-material'

import SearchHelpTabs from './SearchHelpTabs/SearchHelpTabs'
import { useIsMobile } from '@edge/spark/ui-mui'

interface HelperInfoProps {
    flex?: boolean
}

export interface DialogTitleProps {
    children: React.ReactNode
    onClose: () => void
}

const DialogTitle: React.VFC<DialogTitleProps> = ({ children, onClose, ...rest }: DialogTitleProps) => {
    return (
        <MuiDialogTitle sx={{ margin: 0, padding: 2, textAlign: 'center' }} {...rest}>
            <Typography style={{ fontWeight: 'bold' }} variant="h6">
                {children}
            </Typography>
            {onClose ? (
                <IconButton
                    color="primary"
                    aria-label="close"
                    sx={{
                        position: 'absolute',
                        right: '8px',
                        top: '8px',
                        p: '12px',
                        color: (theme) => theme.palette.grey[500],
                        zIndex: 1000,
                    }}
                    onClick={onClose}
                >
                    <CloseIcon color="primary" />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    )
}

const HelperInfo: React.VFC<HelperInfoProps> = ({ flex }: HelperInfoProps) => (
    <>
        <div style={{ textAlign: 'center' }}>
            <Button
                color="primary"
                target="_blank"
                size="small"
                rel="noopener noreferrer"
                href="https://support.auctionedge.com/support/solutions/articles/13000073439-spark-search"
            >
                Click here for a list of all search terms
            </Button>
        </div>
        <div style={{ padding: flex ? '0 20px' : '' }}>
            <Typography>
                For a better experience, try some of the search tips listed here. Or enter a stock #, customer account
                #, or type a word or name to search by, such as <strong>Ford</strong> or <strong>Auto Sales</strong> or{' '}
                <strong>Smith</strong>.
            </Typography>
        </div>
    </>
)

const SearchHelpDialog: React.FC = () => {
    const mobile = useIsMobile()
    const [open, setOpen] = React.useState(false)
    const handleClickOpen = () => {
        setOpen(true)
    }
    const handleClose = () => {
        setOpen(false)
    }

    return (
        <div>
            <Tooltip title="Advanced Search Tips">
                <IconButton onClick={handleClickOpen}>
                    <HelpOutlineIcon />
                </IconButton>
            </Tooltip>
            <Dialog
                maxWidth={mobile ? 'sm' : 'md'}
                onClose={handleClose}
                aria-labelledby="customized-dialog-title"
                open={open}
            >
                <Grid container direction={mobile ? 'column' : 'row'} justifyContent="center">
                    <Grid item md={5}>
                        <DialogTitle onClose={handleClose}>Advanced Search Tips</DialogTitle>
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <HelperInfo flex />
                        </Box>
                    </Grid>
                    <Grid item md={7}>
                        <DialogContent sx={{ p: '20px 16px 16px' }} dividers>
                            <Box sx={{ display: { sm: 'none' } }}>
                                <HelperInfo />
                            </Box>
                            <SearchHelpTabs />
                        </DialogContent>
                    </Grid>
                </Grid>
            </Dialog>
        </div>
    )
}

export default SearchHelpDialog
